import { FormattingSettings, ProjectSettings } from '@trimble/types';
import { Client } from '@hey-api/client-fetch';
import {
  mapToDomain,
  mapToTcpsModel,
} from '../helpers/project-settings/project-settings-mapper';
import { handleResult } from '../helpers/project-settings/tcps-response-helper';
import {
  updateProjectSettings as tcpsUpdateProjectSettings,
  getProjectsettings as tcpsGetProjectsettings,
  UpdateProjectSettingsError,
  UpdateProjectSettingsResponse,
  GetProjectSettingsResponse,
  GetProjectsettingsError,
} from '../generated/index';
import {
  mapFormatSettingsToUnitSettings,
  mapUnitSettingsToFormatSettings,
} from '../helpers/formatting-settings/formatting-settings-helper';

/**
 * Service to manage project settings using the Trimble Connect Project Service (TCPS) API.
 */
export class TcpsProjectSettingsService {
  constructor(private readonly client: Client) {}

  /**
   * Get the formatting settings for a project via the TCPS API.
   * @param projectId - The ID of the project to retrieve formatting settings for.
   * @returns A promise that resolves to the FormattingSettings or null if an error occurs.
   */
  public async getFormattingSettings(
    projectId: string
  ): Promise<FormattingSettings | null | Error> {
    return await this.getProjectSettings(projectId).then((value) => {
      if (value instanceof Error) {
        return value;
      } else {
        return value.unitSettings
          ? mapUnitSettingsToFormatSettings(value.unitSettings)
          : null;
      }
    });
  }

  /**
   * Update the formatting settings for a project via the TCPS API.
   * @param projectId - The ID of the project to update.
   * @param settings - The formatting settings to update.
   * @returns A promise that resolves to true if the formatting settings were updated successfully, or an error if an error occurs.
   */
  public async updateFormattingSettings(
    projectId: string,
    settings: FormattingSettings
  ): Promise<boolean | Error> {
    return await this.updateProjectSettings(projectId, {
      unitSettings: mapFormatSettingsToUnitSettings(settings),
    });
  }

  /**
   * Patch update a projects settings via the TCPS API.
   *
   * @param projectId - The ID of the project to update.
   * @param settings - The settings to update which may include unit settings,
   *                   sync settings, notification settings and the project restrictions.
   * @returns A promise that resolves to the updated ProjectSettings or null if an error occurs.
   */
  public async updateProjectSettings(
    projectId: string,
    settings: ProjectSettings
  ): Promise<boolean | Error> {
    return await tcpsUpdateProjectSettings({
      path: {
        projectId: projectId,
      },
      body: mapToTcpsModel(settings),
      client: this.client,
    })
      .then((value) =>
        handleResult<UpdateProjectSettingsResponse, UpdateProjectSettingsError>(
          value
        )
      )
      // eslint-disable-next-line sonarjs/function-return-type -- union return type is intentional
      .then((value) => {
        if (value instanceof Error) {
          return value;
        } else {
          return true;
        }
      })
      .catch((error) => {
        return new Error('Failed to update project settings.', {
          cause: error,
        });
      });
  }

  /**
   * Get the settings for a project via the TCPS API.
   * Currently, this includes unit settings, sync settings, notification settings and the project restrictions.
   * @param projectId - The Id of the project to retrieve settings for. This may be a Works Manager project
   *  or a Trimble Connect project depending on where the API is hosted.
   * @returns A promise that resolves to the ProjectSettings or null if an error occurs.
   */
  public async getProjectSettings(
    projectId: string
  ): Promise<ProjectSettings | Error> {
    return await tcpsGetProjectsettings({
      path: {
        projectId: projectId,
      },
      client: this.client,
    })
      .then((value) =>
        handleResult<GetProjectSettingsResponse, GetProjectsettingsError>(value)
      )
      .then((value) => {
        if (value instanceof Error) {
          return value;
        } else {
          return mapToDomain(value);
        }
      })
      .catch((error) => {
        return new Error('Failed to get project settings.', { cause: error });
      });
  }
}
